import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, Phone, LogIn, LogOut, Settings, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultNavLinks = [
  { path: '/', label: 'Accueil' },
  { path: '/mascottes', label: 'Animation Mascotte' },
  { path: '/anniversaire', label: 'Anniversaire' },
  { path: '/spectacles', label: 'Spectacles' },
  { path: '/animations', label: 'Animations +' },
  { path: '/location', label: 'Location Machines' },
  { path: '/blog', label: 'Blog' },
  { path: '/contact', label: 'Contact' },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditingMenu, setIsEditingMenu] = useState(false);
  const [navLinks, setNavLinks] = useState(defaultNavLinks);
  const location = useLocation();
  const { isAdmin, logout, getAuthHeaders } = useAuth();

  useEffect(() => {
    loadNavLinks();
  }, []);

  const loadNavLinks = async () => {
    try {
      const res = await axios.get(`${API}/content/header/navlinks`);
      if (res.data?.items && res.data.items.length > 0) {
        setNavLinks(res.data.items);
      }
    } catch (e) {}
  };

  const saveNavLinks = async (newLinks) => {
    try {
      await axios.put(`${API}/content/header/navlinks`, { content: { items: newLinks } }, { headers: getAuthHeaders() });
      setNavLinks(newLinks);
      toast.success('Menu mis à jour !');
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const moveLink = (index, direction) => {
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= navLinks.length) return;
    const newLinks = [...navLinks];
    [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];
    saveNavLinks(newLinks);
  };

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_magie-enfants/artifacts/prlprfaq_Mesa%20de%20trabajo3.png" 
              alt="Le Clown Magicien" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" data-testid="desktop-nav">
            {navLinks.map((link, index) => (
              <div key={link.path} className="relative flex items-center group">
                {isAdmin && isEditingMenu && (
                  <button
                    onClick={() => moveLink(index, 'left')}
                    disabled={index === 0}
                    className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs disabled:opacity-30 mr-1"
                    title="Déplacer à gauche"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                )}
                <Link
                  to={link.path}
                  className={`nav-link px-4 py-2 text-sm font-semibold rounded-full transition-all duration-300 ${
                    isActive(link.path) 
                      ? 'text-primary bg-primary/10' 
                      : 'text-foreground hover:text-primary hover:bg-primary/5'
                  } ${isAdmin && isEditingMenu ? 'bg-blue-50 rounded-lg' : ''}`}
                  data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
                {isAdmin && isEditingMenu && (
                  <button
                    onClick={() => moveLink(index, 'right')}
                    disabled={index === navLinks.length - 1}
                    className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs disabled:opacity-30 ml-1"
                    title="Déplacer à droite"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button & Admin */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href="tel:0635512582" 
              className="flex items-center gap-2 text-sm font-semibold text-primary"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              06 35 51 25 82
            </a>
            
            {isAdmin ? (
              <>
                <Button
                  variant={isEditingMenu ? "default" : "outline"}
                  size="sm"
                  onClick={() => setIsEditingMenu(!isEditingMenu)}
                  className={`rounded-full gap-2 ${isEditingMenu ? 'bg-blue-500 hover:bg-blue-600' : 'border-blue-500 text-blue-600'}`}
                >
                  <GripVertical className="w-4 h-4" />
                  {isEditingMenu ? 'Terminer' : 'Réorganiser menu'}
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="rounded-full gap-2 border-green-500 text-green-600"
                      data-testid="admin-menu-btn"
                    >
                      <Settings className="w-4 h-4" />
                      Admin
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={handleLogout} className="gap-2 text-red-600">
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : null}

            <Link to="/contact">
              <Button 
                className="btn-bounce rounded-full bg-primary hover:bg-primary/90 text-white font-semibold px-6"
                data-testid="header-cta"
              >
                Réserver
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-white/50 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 shadow-lg" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                  isActive(link.path) 
                    ? 'bg-primary/10 text-primary' 
                    : 'hover:bg-white/50'
                }`}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
            
            {isAdmin && (
              <button
                onClick={() => {
                  handleLogout();
                  setIsMenuOpen(false);
                }}
                className="w-full px-4 py-3 rounded-xl text-base font-semibold text-red-600 hover:bg-red-50 text-left flex items-center gap-2"
              >
                <LogOut className="w-5 h-5" />
                Déconnexion Admin
              </button>
            )}
            
            <div className="pt-4 border-t border-border">
              <a 
                href="tel:0635512582" 
                className="flex items-center justify-center gap-2 py-3 text-primary font-semibold"
              >
                <Phone className="w-5 h-5" />
                06 35 51 25 82
              </a>
            </div>
          </nav>
        </div>
      )}

      {/* Admin Mode Indicator */}
      {isAdmin && (
        <div className="bg-green-500 text-white text-center py-1 text-xs font-semibold">
          Mode Administration - Survolez les éléments pour les modifier
        </div>
      )}
    </header>
  );
};
